import {
  ExpandLess,
  ExpandMore,
  InfoOutline,
  MoreHoriz,
} from '@mui/icons-material';
import {
  Box,
  ButtonBase,
  Checkbox,
  CircularProgress,
  Table as MuiTable,
  TableCell as MuiTableCell,
  Radio,
  styled,
  SvgIconTypeMap,
  TableBody,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from '@mui/material';
import { OverridableComponent } from '@mui/material/OverridableComponent';
import { isFunction } from 'lodash';
import { ChangeEvent, Fragment, MouseEvent, ReactNode } from 'react';
import { useContainerWidth } from '../../hooks/useContainerWidth';
import { Column as LayoutColumn, Row } from '../Layout';
import TableActions, { ActionsProps } from './TableActions';
import TableSkeleton from './TableSkeleton';
import { Column } from './types';
import useColumnsWithWidth from './useColumnsWithWidth';
import ColorIndicator from '../ColorIndicator';
import Avatar from '../Avatar';

export type { Column };

const ACTIONS_COLUMN_WIDTH = 52;

const Wrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'darkBackground',
})<{ darkBackground?: boolean }>(({ theme, darkBackground }) => ({
  backgroundColor: darkBackground ? theme.palette.grey[50] : 'transparent',
}));

const Container = styled(TableContainer)({
  position: 'relative',
  overflow: 'auto',
});

const RelativeTableBody = styled(TableBody)({
  position: 'relative',
});

const PaddedBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(theme.spacingSizes.xs),
}));

export const StyledTable = styled(MuiTable, {
  shouldForwardProp: (prop) =>
    prop !== 'scrollable' && prop !== 'borderless' && prop !== 'hideHeader',
})<{ scrollable?: boolean; borderless?: boolean; hideHeader?: boolean }>(
  ({ theme, scrollable, borderless, hideHeader }) => ({
    ...(scrollable && { tableLayout: 'fixed' }),
    ...(borderless && {
      '& tr:last-of-type td': {
        borderBottom: 'none',
      },
      '& td:first-of-type, & th:first-of-type': {
        borderLeft: 'none',
      },
      '& td:last-of-type, & th:last-of-type': {
        borderRight: 'none',
      },
    }),
    ...(hideHeader && {
      '& > tbody > tr:first-of-type > td': {
        borderTop: `1px solid ${theme.palette.grey[300]}`,
      },
    }),
  }),
);

const StyledTableRow = styled(TableRow, {
  shouldForwardProp: (prop) =>
    prop !== 'isDisabled' &&
    prop !== 'isExpanded' &&
    prop !== 'rowBackgroundColor',
})<{
  isDisabled: boolean;
  isExpanded: boolean;
  onClick?: () => void;
  rowBackgroundColor?: string;
}>(({ isDisabled, isExpanded, onClick, rowBackgroundColor }) => ({
  opacity: isDisabled ? 0.5 : 1,
  pointerEvents: 'auto',
  cursor: onClick ? 'pointer' : 'default',
  backgroundColor: rowBackgroundColor,
  '& .MuiTableCell-root': {
    color: isDisabled ? 'text.disabled' : 'inherit',
    borderBottom: isExpanded ? 'none' : undefined,
    backgroundColor: rowBackgroundColor,
  },
  '& .MuiTableCell-root > *': {
    pointerEvents: isDisabled ? 'none' : 'auto',
  },
  '& .MuiIconButton-root': {
    pointerEvents: isDisabled ? 'none' : 'auto',
  },
}));

const EmptyMessageCell = styled(MuiTableCell)(({ theme }) => ({
  padding: theme.spacing(20, theme.spacingSizes.sm),
}));

const CheckboxCell = styled(MuiTableCell)(({ theme }) => ({
  padding: theme.spacing(0, theme.spacingSizes.xs),
  width: 50,
}));

const TableBackdrop = styled(Box)(({ theme }) => ({
  zIndex: theme.zIndex.drawer + 1,
  position: 'absolute',
  width: '100%',
  height: '100%',
  top: 0,
  left: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: theme.palette.common.white,
  opacity: 0.4,
}));

const TooltipIcon = styled(InfoOutline)(({ theme }) => ({
  fontSize: theme.fontSizes.lg,
  color: theme.palette.grey[500],
}));

const TableTitle = styled(Box)(({ theme }) => ({
  padding: theme.spacing(theme.spacingSizes.sm),
  border: `1px solid ${theme.palette.grey[300]}`,
}));

const HeaderRow = styled(TableRow, {
  shouldForwardProp: (prop) =>
    prop !== 'withBorder' && prop !== 'withTopBorder',
})<{ withBorder?: boolean; withTopBorder?: boolean }>(
  ({ theme, withBorder, withTopBorder }) => ({
    ...(withBorder && {
      '& th:first-of-type': {
        borderLeft: `1px solid ${theme.palette.grey[300]}`,
      },
      '& th:last-of-type': {
        borderRight: `1px solid ${theme.palette.grey[300]}`,
      },
    }),
    ...(withTopBorder && {
      '& th': {
        borderTop: `1px solid ${theme.palette.grey[300]}`,
      },
    }),
  }),
);

export const TableCell = styled(MuiTableCell, {
  shouldForwardProp: (prop) =>
    prop !== 'paddingY' &&
    prop !== 'noWrap' &&
    prop !== 'sticky' &&
    prop !== 'isHeaderCell' &&
    prop !== 'withVerticalBorder' &&
    prop !== 'isFooterCell' &&
    prop !== 'darkBackground' &&
    prop !== 'edge' &&
    prop !== 'centered',
})<{
  paddingY?: number;
  edge?: 'start' | 'end';
  noWrap?: boolean;
  sticky?: boolean;
  left?: number;
  isHeaderCell?: boolean;
  isFooterCell?: boolean;
  withVerticalBorder?: boolean;
  darkBackground?: boolean;
  centered?: boolean;
}>(
  ({
    theme,
    paddingY,
    noWrap,
    width,
    sticky,
    left,
    isHeaderCell,
    withVerticalBorder,
    isFooterCell,
    darkBackground,
    edge,
    centered,
  }) => ({
    fontSize: theme.typography.h6.fontSize,
    fontWeight: theme.typography.h6.fontWeight,
    color: theme.typography.h6.color,
    width,
    ...(typeof paddingY === 'number' && {
      paddingTop: paddingY,
      paddingBottom: paddingY,
    }),
    ...(typeof edge === 'string' && {
      paddingLeft: edge === 'start' ? 0 : undefined,
      paddingRight: edge === 'end' ? 0 : undefined,
    }),
    ...(sticky && {
      position: 'sticky',
      left,
      backgroundColor: darkBackground
        ? theme.palette.grey[50]
        : theme.palette.background.paper,
      zIndex: isHeaderCell ? 3 : 2,
    }),
    ...(withVerticalBorder && {
      borderRight: `1px solid ${theme.palette.grey[300]}`,
    }),
    ...(isFooterCell && {
      color: theme.typography.body2.color,
    }),
    whiteSpace: noWrap ? 'nowrap' : 'normal',
    backgroundColor: darkBackground ? theme.palette.grey[50] : undefined,
    ...(centered && {
      '&&': { verticalAlign: 'middle', textAlign: 'center' },
    }),
  }),
);

const SubRowCell = styled(TableCell)(({ theme }) => ({
  backgroundColor: theme.palette.grey[50],
  fontWeight: theme.typography.body2.fontWeight,
}));

const ExpandedContentCell = styled(MuiTableCell)(() => ({
  padding: 0,
}));

const ExpandedContentWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'tableWidth',
})<{ tableWidth: number | null }>(({ tableWidth }) => ({
  ...(tableWidth && {
    maxWidth: tableWidth - 2,
    position: 'sticky',
    left: 0,
    width: tableWidth - 2,
  }),
}));

const Table = <T extends { id: string }, OrderByOption extends string>({
  title,
  columns,
  rows,
  stickyHeader = true,
  pagination,
  isLoading = false,
  rowsTotalCount = 0,
  emptyMessage,
  disableSorting = false,
  actions,
  actionsHandleIcon = MoreHoriz,
  isDisabledRow,
  isExpandedRow,
  isExpandableRow,
  getExpandedContent,
  getExpandedRows,
  disabledRowTooltip,
  customFooterRow,
  scrollable = false,
  onRowClick,
  showHeaderBorder = false,
  showTopBorder = false,
  darkBackground = false,
  borderless = false,
  onRowExpand,
  rowHasPendingAction,
  getRowBackgroundColor,
  hidePaginationForSinglePage = false,
  rowsPerPageOptions = [10, 25, 50, 100],
  checkboxes,
  hideHeader = false,
}: {
  title?: string | ReactNode;
  columns: Array<Column<T>>;
  rows: Array<T>;
  stickyHeader?: boolean;
  isLoading?: boolean;
  emptyMessage?: string | ReactNode;
  rowsPerPageOptions?: Array<number>;
  hidePaginationForSinglePage?: boolean;
  pagination?: {
    page: number;
    rowsPerPage: number;
    orderBy: OrderByOption;
    order: 'asc' | 'desc';
    handleRequestSort: (
      event: MouseEvent<HTMLSpanElement>,
      columnKey: OrderByOption,
    ) => void;
    handleChangePage: (
      e: MouseEvent<HTMLButtonElement> | null,
      page: number,
    ) => void;
    handleChangeRowsPerPage: (e: ChangeEvent<HTMLInputElement>) => void;
  };
  customFooterRow?: Array<{
    key: string;
    colspan?: number;
    content: ReactNode;
  }>;
  disableSorting?: boolean;
  rowsTotalCount?: number;
  scrollable?: boolean;
  actions?: ActionsProps<T>['actions'];
  rowHasPendingAction?: (row: T) => boolean;
  actionsHandleIcon?: OverridableComponent<SvgIconTypeMap<object>>;
  isDisabledRow?: (row: T) => boolean;
  isExpandedRow?: (row: T) => boolean;
  getExpandedContent?: (
    row: T,
    tableWidth: number | null,
    rowIndex: number,
  ) => ReactNode;
  getExpandedRows?: (row: T) => Array<{
    key: string;
    columns: Array<{ key: string; content: ReactNode }>;
  }>;
  disabledRowTooltip?: string | ReactNode | ((row: T) => string | ReactNode);
  onRowClick?: (row: T) => void;
  onRowExpand?: (row: T) => void;
  checkboxes?: {
    isCheckedRow: (row: T) => boolean;
    onRowCheckedToggle: (row: T) => void;
    allRowsChecked?: boolean;
    onCheckAllToggle?: (checked: boolean) => void;
    variant?: 'checkbox' | 'radio';
  };
  isExpandableRow?: (row: T) => boolean;
  showHeaderBorder?: boolean;
  showTopBorder?: boolean;
  darkBackground?: boolean;
  borderless?: boolean;
  getRowBackgroundColor?: (row: T) => string | undefined;
  hideHeader?: boolean;
}): JSX.Element => {
  const showSkeleton = isLoading && rows.length === 0;
  const showBackdrop = isLoading && !showSkeleton;

  const { width, ref } = useContainerWidth();

  const { order, orderBy, handleRequestSort } = pagination ?? {
    order: 'asc',
    orderBy: columns[0]?.key ?? '',
  };

  const enableSorting = !!orderBy && !!handleRequestSort && !disableSorting;

  const getSortOrderChangeHandler =
    (columnKey: OrderByOption) => (event: MouseEvent<HTMLSpanElement>) => {
      handleRequestSort?.(event, columnKey);
    };

  const enableStickyHeader = stickyHeader && !!rows.length;
  const showEmptyMessage = !isLoading && rows.length === 0 && !!emptyMessage;

  const hasCheckboxes = Boolean(checkboxes);
  const checkboxesVariant = checkboxes?.variant ?? 'checkbox';
  const hasActions = !!actions?.length || onRowExpand;
  const hasVisibleActions = actions?.some((action) => !action.hidden);
  const columnsCount =
    columns.length + (hasActions ? 1 : 0) + (hasCheckboxes ? 1 : 0);

  const getColumnLabel = (column: Column<T>) => {
    return column.tooltip ? (
      <Tooltip title={column.tooltip} arrow>
        <Box>
          <Row centered spacing="xxs">
            {column.label} <TooltipIcon />
          </Row>
        </Box>
      </Tooltip>
    ) : (
      <>{column.label}</>
    );
  };

  const resolvedColumns = useColumnsWithWidth(columns, width);

  const renderTableHeaderCell = (column: Column<T>, index: number) => (
    <TableCell
      edge={
        index === 0 && hasCheckboxes && !column.displayAfterActions
          ? 'start'
          : undefined
      }
      size="small"
      isHeaderCell
      key={column.key}
      sticky={column.sticky}
      sortDirection={
        enableSorting ? (orderBy === column.key ? order : false) : false
      }
      withVerticalBorder={column.withVerticalBorder}
      left={column.left}
      width={column.width}
      noWrap={column.noWrap}
      paddingY={column.label ? column.paddingY : 0}
      colSpan={column.colspan}
      darkBackground={darkBackground}
    >
      {enableSorting ? (
        <TableSortLabel
          active={column.key ? orderBy === column.key : false}
          direction={orderBy === column.key ? order : 'asc'}
          onClick={getSortOrderChangeHandler(column.key as OrderByOption)}
          disabled={isLoading || column.disableSorting}
          IconComponent={ExpandMore}
        >
          <Typography variant="inherit" color="grey.500">
            {getColumnLabel(column)}
          </Typography>
        </TableSortLabel>
      ) : (
        <Typography variant="inherit" color="grey.500">
          {getColumnLabel(column)}
        </Typography>
      )}
    </TableCell>
  );

  const renderTableBodyCell = (
    column: Column<T>,
    columnIndex: number,
    row: T,
    rowIndex: number,
  ) => {
    const hasCustomRender = !!column.render;
    const renderedContent = column.render?.(row, rowIndex);
    const colorIndicator = column.renderColorIndicator?.(row, rowIndex);

    const content = hasCustomRender ? (
      typeof renderedContent === 'string' ? (
        <Typography variant="h6">{renderedContent}</Typography>
      ) : (
        renderedContent
      )
    ) : (
      <Typography variant="h6">
        {row[column.key as keyof T] as ReactNode}
      </Typography>
    );

    return (
      <TableCell
        edge={
          columnIndex === 0 && hasCheckboxes && !column.displayAfterActions
            ? 'start'
            : undefined
        }
        sticky={column.sticky}
        size="medium"
        left={column.left}
        width={column.width}
        key={`${row.id}-${column.key}`}
        noWrap={column.noWrap}
        paddingY={column.paddingY}
        withVerticalBorder={column.withVerticalBorder}
        colSpan={column.colspan}
        centered={column.centered}
      >
        {colorIndicator ? (
          <Row centered spacing="xs">
            <PaddedBox>
              <ColorIndicator
                color={colorIndicator.color}
                colorKey={colorIndicator.colorKey}
                listIndex={rowIndex}
              />
            </PaddedBox>
            {colorIndicator.content}
          </Row>
        ) : column.renderAvatar ? (
          (() => {
            const avatar = column.renderAvatar(row, rowIndex);
            return (
              <Row centered spacing="xs">
                <Avatar
                  size={avatar.size ?? 'md'}
                  text={avatar.text}
                  color="random"
                />
                <LayoutColumn>
                  <Typography variant="inherit">
                    {avatar.text}
                  </Typography>
                  {avatar.subtext ? (
                    <Typography variant="body2" color="grey.600">
                      {avatar.subtext}
                    </Typography>
                  ) : null}
                </LayoutColumn>
              </Row>
            );
          })()
        ) : (
          content
        )}
      </TableCell>
    );
  };

  const renderTableFooterCell = (column: Column<T>, index: number) => (
    <TableCell
      edge={
        index === 0 && hasCheckboxes && !column.displayAfterActions
          ? 'start'
          : undefined
      }
      sticky={column?.sticky}
      size="medium"
      left={column?.left}
      width={column?.width}
      noWrap={column?.noWrap}
      paddingY={column?.paddingY}
      withVerticalBorder={column?.withVerticalBorder}
      isFooterCell
      key={column?.key}
      colSpan={column?.colspan}
    >
      {column.footer ?? null}
    </TableCell>
  );

  const hasFooter =
    rows.length > 0 &&
    (columns.some((column) => column.footer) || !!customFooterRow);

  const shouldShowPagination =
    pagination &&
    !!rowsTotalCount &&
    (!hidePaginationForSinglePage ||
      Math.ceil(rowsTotalCount / pagination.rowsPerPage) > 1);

  return (
    <Wrapper ref={ref} darkBackground={darkBackground}>
      {title ? (
        <TableTitle>
          {typeof title === 'string' ? (
            <Typography variant="h6">{title}</Typography>
          ) : (
            title
          )}
        </TableTitle>
      ) : null}
      <Container>
        <StyledTable
          scrollable={scrollable}
          stickyHeader={enableStickyHeader}
          borderless={borderless}
          hideHeader={hideHeader}
        >
          {hideHeader ? null : (
            <TableHead>
              <HeaderRow
                withTopBorder={showTopBorder}
                withBorder={!!title || showHeaderBorder}
              >
                {checkboxes ? (
                  <CheckboxCell>
                    {checkboxes.onCheckAllToggle &&
                    checkboxesVariant === 'checkbox' ? (
                      <Checkbox
                        size="small"
                        checked={checkboxes.allRowsChecked ?? false}
                        onChange={(_e, checked) => {
                          checkboxes.onCheckAllToggle?.(checked);
                        }}
                      />
                    ) : null}
                  </CheckboxCell>
                ) : null}
                {resolvedColumns
                  .filter((column) => !column.displayAfterActions)
                  .map(renderTableHeaderCell)}
                {hasActions ? (
                  <TableCell
                    size="medium"
                    key="actions"
                    width={
                      onRowExpand && hasVisibleActions
                        ? ACTIONS_COLUMN_WIDTH * 2
                        : ACTIONS_COLUMN_WIDTH
                    }
                    darkBackground={darkBackground}
                  />
                ) : null}
                {resolvedColumns
                  .filter((column) => column.displayAfterActions)
                  .map(renderTableHeaderCell)}
              </HeaderRow>
            </TableHead>
          )}
          <RelativeTableBody>
            {showEmptyMessage ? (
              <TableRow>
                <EmptyMessageCell colSpan={columnsCount}>
                  {typeof emptyMessage === 'string' ? (
                    <Typography
                      variant="body2"
                      textAlign="center"
                      color="grey.500"
                    >
                      {emptyMessage}
                    </Typography>
                  ) : (
                    emptyMessage
                  )}
                </EmptyMessageCell>
              </TableRow>
            ) : null}
            {showSkeleton ? (
              <TableSkeleton
                columns={[
                  ...resolvedColumns.filter((col) => !col.displayAfterActions),
                  ...(hasActions ? [{ key: 'actions' }] : []),
                  ...resolvedColumns.filter((col) => col.displayAfterActions),
                ]}
                rowsPerPage={pagination?.rowsPerPage}
              />
            ) : (
              <>
                {rows.map((row, rowIndex) => {
                  const isDisabled = isDisabledRow?.(row) ?? false;
                  const isExpanded = isExpandedRow?.(row) ?? false;
                  const hasPendingAction = rowHasPendingAction?.(row) ?? false;
                  const rowBackgroundColor = getRowBackgroundColor?.(row);
                  const canExpand = isExpandableRow?.(row) ?? false;

                  const rowContent = (
                    <Fragment key={row.id}>
                      <StyledTableRow
                        isDisabled={isDisabled}
                        isExpanded={isExpanded}
                        rowBackgroundColor={rowBackgroundColor}
                        onClick={
                          onRowClick
                            ? () => {
                                if (isDisabled) {
                                  return;
                                }
                                onRowClick(row);
                              }
                            : undefined
                        }
                        hover={!!onRowClick}
                      >
                        {checkboxes ? (
                          <CheckboxCell>
                            {checkboxesVariant === 'checkbox' ? (
                              <Checkbox
                                checked={checkboxes.isCheckedRow(row)}
                                size="small"
                                onChange={() => {
                                  checkboxes.onRowCheckedToggle(row);
                                }}
                              />
                            ) : (
                              <Radio
                                checked={checkboxes.isCheckedRow(row)}
                                size="small"
                                onChange={() => {
                                  checkboxes.onRowCheckedToggle(row);
                                }}
                              />
                            )}
                          </CheckboxCell>
                        ) : null}
                        {resolvedColumns
                          .filter((column) => !column.displayAfterActions)
                          .map((column, columnIndex) =>
                            renderTableBodyCell(
                              column,
                              columnIndex,
                              row,
                              rowIndex,
                            ),
                          )}
                        {hasActions ? (
                          <TableCell
                            size="medium"
                            key={`${row.id}-actions`}
                            width={ACTIONS_COLUMN_WIDTH}
                          >
                            <Row spacing="xs" centered>
                              {hasPendingAction ? (
                                <CircularProgress size={15} />
                              ) : null}
                              <TableActions
                                actions={actions}
                                row={row}
                                handleIcon={actionsHandleIcon}
                              />
                              {onRowExpand && canExpand ? (
                                <ButtonBase
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onRowExpand(row);
                                  }}
                                >
                                  {isExpanded ? (
                                    <ExpandLess fontSize="small" />
                                  ) : (
                                    <ExpandMore fontSize="small" />
                                  )}
                                </ButtonBase>
                              ) : null}
                            </Row>
                          </TableCell>
                        ) : null}
                        {resolvedColumns
                          .filter((column) => column.displayAfterActions)
                          .map((column, columnIndex) =>
                            renderTableBodyCell(
                              column,
                              columnIndex,
                              row,
                              rowIndex,
                            ),
                          )}
                      </StyledTableRow>
                      {isExpanded ? (
                        getExpandedContent ? (
                          <TableRow>
                            <ExpandedContentCell colSpan={columnsCount}>
                              <ExpandedContentWrapper tableWidth={width}>
                                {getExpandedContent(row, width, rowIndex)}
                              </ExpandedContentWrapper>
                            </ExpandedContentCell>
                          </TableRow>
                        ) : getExpandedRows ? (
                          getExpandedRows(row).map((subRow) => (
                            <TableRow key={subRow.key}>
                              {subRow.columns.map((column) => (
                                <SubRowCell key={column.key}>
                                  {column.content}
                                </SubRowCell>
                              ))}
                            </TableRow>
                          ))
                        ) : null
                      ) : null}
                    </Fragment>
                  );

                  if (isDisabled) {
                    return (
                      <Tooltip
                        key={row.id}
                        title={
                          isFunction(disabledRowTooltip)
                            ? disabledRowTooltip(row)
                            : disabledRowTooltip
                        }
                        placement="top"
                      >
                        {rowContent}
                      </Tooltip>
                    );
                  }

                  return rowContent;
                })}
                {hasFooter ? (
                  customFooterRow ? (
                    <TableRow>
                      {hasCheckboxes ? (
                        <TableCell key="footer-checkbox" />
                      ) : null}
                      {customFooterRow.map((footer) => (
                        <TableCell colSpan={footer.colspan} key={footer.key}>
                          {footer.content}
                        </TableCell>
                      ))}
                    </TableRow>
                  ) : (
                    <TableRow>
                      {hasCheckboxes ? (
                        <TableCell key="footer-checkbox" />
                      ) : null}
                      {resolvedColumns
                        .filter((column) => !column.displayAfterActions)
                        .map((column, index) =>
                          renderTableFooterCell(column, index),
                        )}
                      {hasActions ? (
                        <TableCell
                          size="medium"
                          key="footer-actions"
                          width={ACTIONS_COLUMN_WIDTH}
                        />
                      ) : null}
                      {resolvedColumns
                        .filter((column) => column.displayAfterActions)
                        .map((column, index) =>
                          renderTableFooterCell(column, index),
                        )}
                    </TableRow>
                  )
                ) : null}
              </>
            )}
          </RelativeTableBody>
        </StyledTable>
        {showBackdrop ? (
          <TableBackdrop>
            <CircularProgress color="inherit" />
          </TableBackdrop>
        ) : null}
      </Container>
      {shouldShowPagination ? (
        <TablePagination
          rowsPerPageOptions={rowsPerPageOptions}
          component="div"
          count={rowsTotalCount}
          rowsPerPage={pagination.rowsPerPage}
          page={pagination.page - 1}
          disabled={isLoading}
          onPageChange={pagination.handleChangePage}
          onRowsPerPageChange={pagination.handleChangeRowsPerPage}
        />
      ) : null}
    </Wrapper>
  );
};

export default Table;
